import React, {useMemo} from 'react';
import clsx from 'clsx';
import {i18n} from '@netx/core/i18n';
import {enumToValues} from '@netx/core-next/utils/enum';
import {SelectField} from '@netx/core-components/SelectField';
import {TextField} from '@netx/core-components/TextField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {SearchSubTypeLabel} from '../../../../locales';
import {CommonRuleEditorProps} from '../../../types';
import {ContentsType} from '../../constants';
import {getContentsTypeLabel} from '../../utils';
import {
	contentsRuleEditorClasses,
	getContentsRuleEditorUtilityClass,
	ContentsRuleEditorClasses,
	CONTENTS_RULE_EDITOR_CLASS_NAME,
} from './contentsRuleEditorClasses';

export type ContentsRuleEditorOwnerState = WithClasses<
	ContentsRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: ContentsRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getContentsRuleEditorUtilityClass, classes);
};

const ContentsRuleEditorRoot = styled('div', {
	name: CONTENTS_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	'& > * + *': {
		marginLeft: `${theme.spacing(1)} !important`,
	},
}));

export type ContentsRuleEditorProps = WithClasses<
	ContentsRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: ContentsType;
		elementSubType1Options?: ContentsType[];
		elementValue1: string;
		onSubType1Change: (subType1: ContentsType) => void;
		onValue1Change: (value: string) => void;
	}
>;

const ContentsRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementValue1,
	validationErrors,
	onSubType1Change,
	onValue1Change,
}: ContentsRuleEditorProps) => {
	const ownerState: ContentsRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const _elementSubType1Options = useMemo(
		() => elementSubType1Options || enumToValues(ContentsType),
		[elementSubType1Options],
	);

	return (
		<ContentsRuleEditorRoot className={clsx(classes.root, className)}>
			<SelectField<ContentsType, false, true, false>
				data-testid="contents-rule-sub-type-1"
				disableClearable
				label={SearchSubTypeLabel.MATCH_TYPE}
				name={'rule-sub-type-1'}
				options={_elementSubType1Options}
				value={elementSubType1}
				getOptionLabel={getContentsTypeLabel}
				onChange={(event, value) => onSubType1Change(value)}
			/>

			<TextField
				data-testid="contents-rule-value-1"
				error={validationErrors?.elementValue1?.message}
				fullWidth
				label={i18n.t('i18n.contents')}
				name="rule-value-1"
				value={elementValue1}
				onChange={(event, value) => onValue1Change(value)}
			/>
		</ContentsRuleEditorRoot>
	);
};

export const ContentsRuleEditorPrimitive = ContentsRuleEditorInner;

export const ContentsRuleEditor = ContentsRuleEditorPrimitive;

export default ContentsRuleEditor;

export type ContentsRuleEditorGenericProps = WithClasses<
	ContentsRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

export const ContentsRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: ContentsRuleEditorGenericProps) => {
	return (
		<ContentsRuleEditor {...props} elementValue1={elementValue1.toString()} />
	);
};

export const ContentsRuleEditorGeneric = ContentsRuleEditorGenericPrimitive;
